import { PointerEvent as ReactPointerEvent, useRef, useState } from 'react';

import useBaseUnitPx from '~/modules/hooks/use-base-unit-px';

// How far a finger has to travel before the gesture commits to an axis. Under this the drag has not
// happened yet, so a vertical scroll inside a panel is never stolen by the tab track.
const AXIS_DECIDED_AT_REM = 0.5;
// Share of the panel width a drag has to pass for letting go to change tab rather than spring back.
const COMMIT_AT = 0.22;
// Past the first and last tab there is nothing to drag to, so the track resists rather than
// following the finger - the alternative reads as a broken screen rather than as an edge.
const EDGE_DAMPING = 0.3;

interface Options {
  tab: number;
  count: number;
  onTabChange: (tab: number) => void;
  // A sheet owns the screen while it is open, and while a song runs the phone is the microphone and
  // nothing else - in both cases a swipe would leave the state it was in.
  disabled: boolean;
}

export default function useTabSwipe({ tab, count, onTabChange, disabled }: Options) {
  const unit = useBaseUnitPx();
  const [dx, setDx] = useState(0);
  const [dragging, setDragging] = useState(false);

  const start = useRef({ x: 0, y: 0 });
  const axis = useRef<'x' | 'y' | null>(null);
  const down = useRef(false);
  const width = useRef(1);
  const track = useRef<HTMLDivElement | null>(null);

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (disabled) return;
    track.current = event.currentTarget;
    width.current = event.currentTarget.offsetWidth || width.current;
    start.current = { x: event.clientX, y: event.clientY };
    axis.current = null;
    down.current = true;
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!down.current) return;
    const movedX = event.clientX - start.current.x;
    const movedY = event.clientY - start.current.y;

    if (axis.current === null) {
      const threshold = unit * AXIS_DECIDED_AT_REM;
      if (Math.abs(movedX) < threshold && Math.abs(movedY) < threshold) return;
      axis.current = Math.abs(movedX) > Math.abs(movedY) ? 'x' : 'y';
      if (axis.current === 'x') {
        setDragging(true);
        // Captured here rather than on pointer-down. While a pointer is captured the browser
        // dispatches the resulting `click` at the capturing element, so capturing up front turns
        // every tap inside a panel into a click on the track and nothing in the panel responds.
        track.current?.setPointerCapture?.(event.pointerId);
      }
    }
    // Handed back to the scroll container, and never taken again for this gesture.
    if (axis.current !== 'x') return;

    const atEdge = (movedX > 0 && tab === 0) || (movedX < 0 && tab === count - 1);
    setDx(atEdge ? movedX * EDGE_DAMPING : movedX);
  };

  const onPointerUp = () => {
    if (!down.current) return;
    down.current = false;
    axis.current = null;

    const threshold = width.current * COMMIT_AT;
    let next = tab;
    if (dx <= -threshold) next = Math.min(count - 1, tab + 1);
    if (dx >= threshold) next = Math.max(0, tab - 1);

    setDx(0);
    setDragging(false);
    if (next !== tab) onTabChange(next);
  };

  // Which tab the underline should already be growing towards. With no labels in the tab bar the
  // underline is the only marker of where the swipe is going, so it moves before the swipe lands.
  const headingFor = dragging && Math.abs(dx) > width.current * COMMIT_AT ? (dx < 0 ? tab + 1 : tab - 1) : tab;

  return {
    dx,
    dragging,
    headingFor: Math.max(0, Math.min(count - 1, headingFor)),
    handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp },
  };
}
